import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material"
import { AddOutlined, DeleteOutlined, EditOutlined, HelpOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import type { ICellRendererParams, IHeaderParams, IRowNode } from "ag-grid-community"
import { type FC, useEffect, useReducer, useRef, useState } from "react"

import { MAX_TIERS_PER_GROUP } from "./constants"
import type { PricingGridRow, RowActionsParams } from "./types"

import { AerosCheckbox } from "@/components/UiKit"
import { UOM_RENDER_PRECISION } from "@/constants/formats"
import { formatNumber } from "@/utils/number"

const Bb: FC<ICellRendererParams<PricingGridRow, number>> = ({ value, data }) => {
  const previousBillBack = data?.item?.previousBillBack
  return (
    <Box className='flex items-center justify-end gap-4'>
      <span>{value !== null && value !== undefined ? `$${formatNumber(value, UOM_RENDER_PRECISION)}` : ""}</span>
      <Tooltip
        title={
          previousBillBack !== null && previousBillBack !== undefined
            ? `Previous BB: $${formatNumber(previousBillBack, UOM_RENDER_PRECISION)}`
            : "No previous BB"
        }
        placement='top'
        arrow
      >
        <HelpOutlined sx={{ fontSize: 14 }} className='cursor-pointer' />
      </Tooltip>
    </Box>
  )
}

const SelectionHeader: FC<IHeaderParams<PricingGridRow>> = ({ api }) => {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    api.addEventListener("selectionChanged", forceUpdate)
    return () => api.removeEventListener("selectionChanged", forceUpdate)
  }, [api])

  const allNodes: IRowNode<PricingGridRow>[] = []
  api.forEachNode((node) => allNodes.push(node))

  const selectedCount = allNodes.filter((node) => node.isSelected()).length
  const allSelected = allNodes.length > 0 && selectedCount === allNodes.length
  const someSelected = selectedCount > 0 && !allSelected

  return (
    <AerosCheckbox
      checked={allSelected}
      indeterminate={someSelected}
      size='small'
      onChange={(checked) => (checked ? api.selectAll() : api.deselectAll())}
      sx={{ padding: 0 }}
    />
  )
}

const Selection: FC<ICellRendererParams<PricingGridRow>> = ({ node, api }) => {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    api.addEventListener("selectionChanged", forceUpdate)
    return () => api.removeEventListener("selectionChanged", forceUpdate)
  }, [api])

  const rowIndex = node.rowIndex
  const itemId = node.data?.item.priceChangeItemId

  // Not the first row in this item group — render nothing
  if (
    rowIndex === null ||
    (rowIndex > 0 && api.getDisplayedRowAtIndex(rowIndex - 1)?.data?.item.priceChangeItemId === itemId)
  ) {
    return <span />
  }

  // Collect all nodes belonging to this item group (by priceChangeItemId)
  const groupNodes: IRowNode<PricingGridRow>[] = []
  for (let i = rowIndex; i < api.getDisplayedRowCount(); i++) {
    const n = api.getDisplayedRowAtIndex(i)
    if (!n?.data || n.data.item.priceChangeItemId !== itemId) break
    groupNodes.push(n)
  }

  const selectedCount = groupNodes.filter((n) => n.isSelected()).length
  const allSelected = groupNodes.length > 0 && selectedCount === groupNodes.length
  const someSelected = selectedCount > 0 && !allSelected

  return (
    <div className='pt-0'>
      <AerosCheckbox
        checked={allSelected}
        indeterminate={someSelected}
        size='small'
        onChange={(checked) => api.setNodesSelected({ nodes: groupNodes, newValue: checked })}
        sx={{ padding: 0 }}
      />
    </div>
  )
}

const RowActions: FC<RowActionsParams> = ({ node, api, onAddPriceTier, onClearPriceTier, onEditSurcharge }) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    api.addEventListener("rowDataUpdated", forceUpdate)
    return () => api.removeEventListener("rowDataUpdated", forceUpdate)
  }, [api])

  const itemId = node.data?.item.priceChangeItemId
  let groupTierCount = 0
  api.forEachNode((n) => {
    if (n.data?.item.priceChangeItemId === itemId) groupTierCount++
  })
  const isAddDisabled = groupTierCount >= MAX_TIERS_PER_GROUP

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const withClose = (fn: () => void) => () => {
    handleMenuClose()
    fn()
  }

  return (
    <Box ref={anchorElRef} className='flex items-center justify-center'>
      <IconButton size='small' onClick={handleMenuOpen}>
        <MoreVertOutlined />
      </IconButton>
      <Menu anchorEl={anchorElRef.current} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={withClose(() => onEditSurcharge(node.rowIndex ?? -1))}>
          <ListItemIcon>
            <EditOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
          </ListItemIcon>
          <ListItemText primary='Edit Surcharge' />
        </MenuItem>
        <Tooltip
          title={isAddDisabled ? `Maximum ${MAX_TIERS_PER_GROUP} tiers per pricing package` : ""}
          placement='left'
        >
          <span>
            <MenuItem disabled={isAddDisabled} onClick={withClose(() => onAddPriceTier(node.rowIndex ?? -1))}>
              <ListItemIcon>
                <AddOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
              </ListItemIcon>
              <ListItemText primary='Add Price Tier' />
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem onClick={withClose(() => onClearPriceTier(node.rowIndex ?? -1))}>
          <ListItemIcon>
            <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
          </ListItemIcon>
          <ListItemText primary='Clear Price Tier' />
        </MenuItem>
      </Menu>
    </Box>
  )
}

export const GridCells = {
  Bb,
  RowActions,
  Selection,
  SelectionHeader,
}
